import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, tokens, type CurrentUser } from './api';
import { dropDesktopNotificationsLocally, forgetDesktopNotifications, resetDesktopPrompt } from './desktop-notifications';

/**
 * Auth state. Permissions come from `/auth/me`, so the UI hides what the server
 * would refuse anyway — the server check (§36.2) is still the real gate; this
 * just stops the interface offering actions that cannot succeed.
 *
 * Signing in names sys, so the session carries the person's sys roles and
 * nothing from the admin. Someone with no sys role is refused at sign-in, with
 * the server's own explanation.
 */

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (...permissions: string[]) => boolean;
  canAny: (...permissions: string[]) => boolean;
  isLeaderOf: (departmentId: string) => boolean;
  ledDepartmentIds: string[];
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  // Whatever one session fetched must never show in the next one on the same
  // browser — salaries least of all, which a Finance viewer may not see. Left
  // in the cache, a page fetched under 20 s ago would be shown as it was.
  const forgetSession = useCallback(() => queryClient.clear(), [queryClient]);

  const loadUser = useCallback(async () => {
    if (!tokens.access) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await api.get<CurrentUser>('/auth/me'));
    } catch {
      tokens.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
    // The API client fires this when a refresh fails, so a dead session clears
    // the UI immediately instead of on the next navigation.
    const onSignedOut = () => {
      forgetSession();
      setUser(null);
      // No session, no notifications on the desktop: the next sign-in here brings them back.
      dropDesktopNotificationsLocally();
    };
    window.addEventListener('viganium:signed-out', onSignedOut);
    return () => window.removeEventListener('viganium:signed-out', onSignedOut);
  }, [loadUser, forgetSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await api.post<{ accessToken: string; refreshToken: string; user: CurrentUser }>(
      '/auth/login',
      { email, password, app: 'SYS' },
    );
    tokens.set(result.accessToken, result.refreshToken);
    // The session before may have ended without a sign-out — an expired token.
    forgetSession();
    // A new sign-in offers desktop notifications again, where they were put off.
    resetDesktopPrompt();
    setUser(result.user);
  }, [forgetSession]);

  const signOut = useCallback(async () => {
    // This browser stops showing their notifications — said while the session still can.
    await forgetDesktopNotifications().catch(() => undefined);
    try {
      await api.post('/auth/logout', { refreshToken: tokens.refresh ?? undefined });
    } catch {
      // Signing out locally matters more than the server round-trip succeeding.
    }
    tokens.clear();
    forgetSession();
    setUser(null);
  }, [forgetSession]);

  const can = useCallback(
    (...permissions: string[]) => permissions.every((p) => user?.permissions.includes(p) ?? false),
    [user],
  );

  const canAny = useCallback(
    (...permissions: string[]) => permissions.some((p) => user?.permissions.includes(p) ?? false),
    [user],
  );

  const ledDepartmentIds = useMemo(
    () => user?.departments.filter((d) => d.memberType === 'LEADER').map((d) => d.id) ?? [],
    [user],
  );

  const isLeaderOf = useCallback(
    (departmentId: string) => ledDepartmentIds.includes(departmentId),
    [ledDepartmentIds],
  );

  const value = useMemo(
    () => ({ user, loading, signIn, signOut, refreshUser: loadUser, can, canAny, isLeaderOf, ledDepartmentIds }),
    [user, loading, signIn, signOut, loadUser, can, canAny, isLeaderOf, ledDepartmentIds],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}

/**
 * Mirrors src/common/permissions.ts on the backend — the permissions a sys role
 * can hold. The admin's never reach a sys session, so they are not listed.
 */
export const P = {
  SYS_ACCESS_MANAGE: 'sys.access.manage',
  // Clients — managed in both systems.
  CLIENTS_VIEW: 'clients.view',
  CLIENTS_CREATE: 'clients.create',
  CLIENTS_UPDATE: 'clients.update',
  CLIENTS_DEACTIVATE: 'clients.deactivate',
  CLIENT_CONTACTS_MANAGE: 'client_contacts.manage',
  // Removing a contact, apart from adding and editing (2026-10-11).
  CLIENT_CONTACTS_REMOVE: 'client_contacts.remove',
  // Finance
  FINANCE_EXPENSES_MANAGE: 'finance.expenses.manage',
  FINANCE_BUDGETS_MANAGE: 'finance.budgets.manage',
  INVOICES_VIEW: 'invoices.view',
  INVOICES_CREATE: 'invoices.create',
  INVOICES_UPDATE: 'invoices.update',
  INVOICES_ISSUE: 'invoices.issue',
  INVOICES_CANCEL: 'invoices.cancel',
  PAYMENTS_VIEW: 'payments.view',
  PAYMENTS_CREATE: 'payments.create',
  PAYMENTS_UPDATE: 'payments.update',
  PAYMENTS_REVERSE: 'payments.reverse',
  CONTRACTS_VIEW: 'contracts.view',
  CONTRACTS_MANAGE: 'contracts.manage',
  // Subscriptions and the external systems using Finance's API (2026-10-28).
  SUBSCRIPTIONS_VIEW: 'subscriptions.view',
  SUBSCRIPTIONS_MANAGE: 'subscriptions.manage',
  EXTERNAL_SYSTEMS_MANAGE: 'external_systems.manage',
  FINANCE_REPORTS_VIEW: 'finance.reports.view',
  FINANCE_EXCHANGE_RATE_EDIT: 'finance.exchange_rate.edit',
  FINANCE_MIGRATION_MANAGE: 'finance.migration.manage',
  FINANCE_SETTINGS_MANAGE: 'finance.settings.manage',
  FINANCE_AUDIT_VIEW: 'finance.audit.view',
  // Payroll
  PAYROLL_MANAGE: 'payroll.manage',
  // Finance Phase 1 (2026-10-28): partners, the historical months, the opening position.
  PARTNERS_VIEW: 'partners.view',
  PARTNERS_MANAGE: 'partners.manage',
  PARTNERS_REVERSE: 'partners.reverse',
  HISTORICAL_FINANCE_VIEW: 'historical_finance.view',
  HISTORICAL_FINANCE_MANAGE: 'historical_finance.manage',
  HISTORICAL_FINANCE_CORRECT: 'historical_finance.correct',
  OPENING_BALANCES_VIEW: 'opening_balances.view',
  OPENING_BALANCES_MANAGE: 'opening_balances.manage',
} as const;
